/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import { Checkbox, InputNumber, message } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import BaseModal from 'src/components/ts-components/BaseModal'
import Text from 'src/components/ts-components/Text'
import { PlayerPositionSelect } from 'src/components/ts-components/PlayerPositionSelect'
import { paragraphXSmall } from 'src/components/css'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

const MIN_SLOTS = 1
const MAX_SLOTS = 60

/**
 * `create` — Add Player Slot: how many, plus the optional Player Finder opt-in.
 * `list` — the row kebab's "List in Player Finder…": the slot already exists and
 * the intent is already "list it", so count and checkbox are hidden and only the
 * position is asked for.
 * `edit-position` — the row kebab's "Edit Player Finder Position" on a slot that
 * is ALREADY listed: same picker, starting from the current position. A position
 * edit, not an opt-in, so it never turns recruiting back on.
 */
export type AddPlayerSlotModalMode = 'create' | 'list' | 'edit-position'

export interface AddPlayerSlotSubmit {
  count: number
  finderListed: boolean
  finderPlayerTypeId: string | null
}

type AddPlayerSlotModalProps = {
  open: boolean
  mode: AddPlayerSlotModalMode
  submitting: boolean
  /**
   * D48 — the server's OPT-IN gate (root finder on + registration approved + no
   * other recruiting registration). False HIDES the checkbox entirely rather than
   * disabling it: a rep usually cannot change a competition-level setting, so a
   * permanently disabled control is noise.
   */
  canListInPlayerFinder: boolean
  /** Drives the position options; absent means no positions can be offered. */
  sportId?: string | null
  /**
   * The team is not recruiting in this competition (never has, or switched it
   * off). Listing a slot turns recruiting on — and re-advertises any other slot
   * already listed — so the two modes that list say so before the rep confirms.
   * Edit position does not change it and says nothing.
   */
  recruitingOff?: boolean
  /** `edit-position` only — the position the slot recruits for now (null = any). */
  initialPlayerTypeId?: string | null
  onSubmit: (values: AddPlayerSlotSubmit) => Promise<unknown>
  onClose: () => void
}

const clampCount = (value: number): number =>
  Math.min(Math.max(Math.round(value || MIN_SLOTS), MIN_SLOTS), MAX_SLOTS)

/**
 * SN-4709 × SN-8081 (D46) — Add Player Slot.
 *
 * Before this, Add Player Slot created one slot instantly with no dialog. The
 * Player Finder opt-in has to be ASKED (Jeff: "you either need to ask them…
 * maybe a checkbox", "it's on a per… per slot"), so the instant-add is replaced
 * by this modal.
 *
 * The checkbox DEFAULTS OFF and every field resets on each open: listing a team
 * publicly is not something a stale piece of local state should carry into the
 * next slot.
 *
 * The Player Finder controls live inside a bordered panel rather than loose in
 * the body. Advertising the team publicly is a different KIND of decision from
 * "how many spots do I need", and the two read as one continuous form when
 * nothing separates them. The panel's help line is shown BEFORE the tick as
 * well as after — a rep should know what the checkbox does without having to
 * try it.
 */
const AddPlayerSlotModal = ({
  open,
  mode,
  submitting,
  canListInPlayerFinder,
  sportId = null,
  recruitingOff = false,
  initialPlayerTypeId = null,
  onSubmit,
  onClose,
}: AddPlayerSlotModalProps) => {
  const isEditPosition = mode === 'edit-position'
  // Both kebab modes act on ONE existing slot and only ask for a position.
  const isSlotMode = mode === 'list' || isEditPosition

  const [count, setCount] = useState<number>(1)
  const [listed, setListed] = useState<boolean>(false)
  const [playerTypeId, setPlayerTypeId] = useState<number | undefined>(
    undefined
  )

  // Reset on EVERY open. Without this a previous tick (or a previous position)
  // survives into the next slot the rep adds and silently advertises it.
  useEffect(() => {
    if (open) {
      const current = Number(initialPlayerTypeId)
      setCount(1)
      setListed(isSlotMode)
      // Edit position starts from what the slot recruits for now; every other
      // mode starts from "any player".
      setPlayerTypeId(
        isEditPosition && initialPlayerTypeId && Number.isFinite(current)
          ? current
          : undefined
      )
    }
  }, [open, isSlotMode, isEditPosition, initialPlayerTypeId])

  const submit = () => {
    onSubmit({
      count: isSlotMode ? 1 : clampCount(count),
      finderListed: isSlotMode ? true : listed,
      finderPlayerTypeId:
        (isSlotMode || listed) && playerTypeId !== undefined
          ? String(playerTypeId)
          : null,
    }).catch((error) => {
      message.error(
        (error as Error)?.message || balanceText('common:balanceAddSlotError')
      )
    })
  }

  const showFinderFields = isSlotMode || (canListInPlayerFinder && listed)
  const resumesRecruiting =
    recruitingOff && (mode === 'list' || (mode === 'create' && listed))

  // Said BEFORE the rep confirms: listing is also what switches recruiting back on.
  const resumeHint = resumesRecruiting ? (
    <p css={resumeHintStyle} data-testid='add-slot-finder-resumes'>
      {balanceText('common:balanceListInFinderResumes')}
    </p>
  ) : null

  const positionPicker = (
    <div>
      <PlayerPositionSelect
        value={playerTypeId}
        onChange={setPlayerTypeId}
        sportId={sportId ?? undefined}
        name='finder_player_type_id'
        label={balanceLabel('common:balanceFinderPositionLabel')}
        allowClear
        placeholder={balanceText('common:balanceFinderPositionAny')}
      />
      <p css={hintStyle}>{balanceText('common:balanceFinderPositionHint')}</p>
    </div>
  )

  return (
    <BaseModal
      isOpen={open}
      onClose={onClose}
      title={balanceLabel(
        isEditPosition
          ? 'common:balanceEditFinderPosition'
          : mode === 'list'
          ? 'common:balanceListInFinder'
          : 'common:balanceAddSlotTitle'
      )}
      desktopWidth='520px'
      destroyOnClose
      actions={
        <div css={footerStyle}>
          <HeaderActionButton
            variant='secondary'
            text={balanceLabel('common:cancel')}
            onClick={onClose}
            disabled={submitting}
            data-testid='add-slot-cancel'
          />
          <HeaderActionButton
            variant='primary'
            text={balanceLabel(isSlotMode ? 'common:save' : 'common:add')}
            onClick={submit}
            isLoading={submitting}
            data-testid='add-slot-submit'
          />
        </div>
      }
    >
      <p css={introStyle}>
        {balanceText(
          isEditPosition
            ? 'common:balanceEditFinderPositionIntro'
            : mode === 'list'
            ? 'common:balanceListInFinderIntro'
            : 'common:balanceAddSlotIntro'
        )}
      </p>

      {!isSlotMode ? (
        <div css={fieldStyle} data-testid='add-slot-count'>
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {balanceLabel('common:balanceAddSlotCount')}
          </Text>
          <InputNumber
            id='add-slot-count'
            aria-label={balanceLabel('common:balanceAddSlotCount')}
            min={MIN_SLOTS}
            max={MAX_SLOTS}
            value={count}
            onChange={(value) =>
              setCount(clampCount(Number(value ?? MIN_SLOTS)))
            }
            css={numberInputStyle}
          />
          <p css={hintStyle}>{balanceText('common:balanceAddSlotCountHint')}</p>
        </div>
      ) : null}

      {/*
        C8/D48 — the panel is hidden entirely (not disabled) when the server says
        this registration cannot advertise. In the two slot modes the intent is
        already "list it", so there is nothing left to tick and the position
        picker stands alone.
      */}
      {isSlotMode ? (
        <>
          {positionPicker}
          {resumeHint}
        </>
      ) : canListInPlayerFinder ? (
        <div css={panelStyle}>
          <Checkbox
            checked={listed}
            onChange={(event) => setListed(event.target.checked)}
            data-testid='add-slot-finder-listed'
          >
            <Text
              variant='paragraphSmall'
              weight='semiBold'
              color={colors.WHITE}
            >
              {balanceLabel('common:balanceListInFinder')}
            </Text>
          </Checkbox>
          {/*
            C4 — the finder card's only action is "View & Register", so this copy
            must NOT promise that anyone will reach out.
          */}
          <p css={panelHelpStyle} data-testid='add-slot-finder-help'>
            {balanceText('common:balanceListInFinderHelp')}
          </p>
          {showFinderFields ? positionPicker : null}
          {resumeHint}
        </div>
      ) : null}
    </BaseModal>
  )
}

export default AddPlayerSlotModal

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`
const introStyle = css`
  ${paragraphXSmall};
  margin: 0;
  color: ${colors.NEUTRAL_40};
  line-height: 1.5;
`
const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`
/** House field treatment — matches TextInput so the modal reads as one form. */
const numberInputStyle = css`
  width: 120px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};

  .ant-input-number-input {
    height: 38px;
    font-size: 14px;
  }
`
/**
 * The opt-in panel. Its own surface + border is what separates "how many spots"
 * from "advertise this team publicly" — two decisions that otherwise run
 * together as one list of controls.
 */
const panelStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_200};
`
const panelHelpStyle = css`
  ${paragraphXSmall};
  margin: 0;
  /* Clears the checkbox glyph so the help reads as its explanation. */
  padding-left: 24px;
  color: ${colors.NEUTRAL_40};
  line-height: 1.5;
`
const hintStyle = css`
  ${paragraphXSmall};
  margin: 4px 0 0;
  color: ${colors.NEUTRAL_50};
`
/**
 * What else confirming does. Neutral, like the panel's own help: for a team that
 * has never advertised, turning recruiting on IS what listing means, so a warning
 * tone would read as a problem.
 */
const resumeHintStyle = css`
  ${paragraphXSmall};
  margin: 0;
  color: ${colors.NEUTRAL_40};
  line-height: 1.5;
`
